import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { useIsNarrowScreen } from '../work/useIsNarrowScreen';
import { useAssetShare } from './useAssetShare';
import { useFocusTrap } from './useFocusTrap';
import { useMakeAgain } from './useMakeAgain';
import { useViewerKeys } from './useViewerKeys';
import { useViewerPost } from './useViewerPost';
import { useViewerToast } from './useViewerToast';
import { AssetViewerItem, viewerIndexOf } from './viewerItems';
import { viewerAssetLabel } from './viewerFacts';
import { viewerMessages } from './viewerMessages';
import { stepIndex } from './viewerNavigation';
import { ViewerConfirmDialog } from './ViewerConfirmDialog';
import { ViewerSidePanel } from './ViewerSidePanel';
import { ViewerStage } from './ViewerStage';
import { ViewerRevisionLayers } from './ViewerRevisionLayers';
import { useViewerRevisions } from './useViewerRevisions';
import { RevisionList } from '../revisions/RevisionList';
import { RevisionGoneBox } from '../revisions/RevisionGoneBox';
import { nextRevisionNumber } from '../revisions/revisionModel';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { useOpenMusicVideoEditor } from '../addedAssets/useOpenMusicVideoEditor';
import { viewerRevisionAction } from './viewerRevisionAction';
import { useGatedAssetShare } from '../../watermarkUpsell/choice/useGatedAssetShare';
import { choiceFileOfViewerItem } from '../../watermarkUpsell/files/choiceFiles';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface AssetViewerProps {
  accountId: number;
  /** The song of every item. */
  song: CatalogItem;
  /** The ready files of the song, in the order of the surface that opens the viewer. */
  items: AssetViewerItem[];
  /** The key of the item to show first (`kitAsset:<uuid>`, `reel:<uuid>`). */
  startKey: string;
  onClose: () => void;
  /** Releases posts a reel ("Post it" on its reel card); the other surfaces do not. */
  allowReelPost?: boolean;
  /**
   * Releases v2 (`?asset=&rev=`, design c2, e4): the revision of the start item to show first. Absent:
   * the start item itself.
   */
  startRevision?: number;
  /** Releases v2 (design c1 "New revision" on a card): the viewer opens with the dialog of the start item (d3). */
  startWithNewRevision?: boolean;
  /**
   * The arrows moved to another item of the list (not called for the start item). The release page
   * writes it into the address, so a refresh opens the item on screen.
   */
  onShownChange?: (item: AssetViewerItem) => void;
}

type OpenViewerProps = Omit<AssetViewerProps, 'startKey'> & { initialKey: string };

/**
 * The open viewer. It keeps the KEY of the shown item, not its place, so a list that changes
 * while it is open (a realtime refetch) keeps the same file on screen.
 */
const OpenViewer: React.FC<OpenViewerProps> = (props) => {
  const {
    accountId,
    song,
    items,
    initialKey,
    onClose,
    allowReelPost = false,
    startRevision,
    startWithNewRevision,
    onShownChange,
  } = props;
  const intl = useIntl();
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentKey, setCurrentKey] = useState(initialKey);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const isNarrow = useIsNarrowScreen();
  const isDemo = useIsDemoAccount();
  const { toast, showToast } = useViewerToast();
  const count = items.length;
  const index = viewerIndexOf(items, currentKey);
  const listItem = items[index] as AssetViewerItem;
  const currentUserId = useUserTokenDecoded()?.id ?? null;
  const revisions = useViewerRevisions({
    accountId,
    catalogItemUuid: song.uuid,
    item: listItem,
    startKey: initialKey,
    startRevision,
    // A music video and an added version have no "New revision" dialog (`viewerRevisionAction`).
    startWithNewRevision: startWithNewRevision === true && viewerRevisionAction(listItem).kind === 'newRevision',
  });
  // Tell the surface about a move to another item; the start item is not a move.
  const lastShownKey = useRef(initialKey);
  useEffect(() => {
    if (listItem.key === lastShownKey.current) {
      return;
    }
    lastShownKey.current = listItem.key;
    onShownChange?.(listItem);
  }, [listItem, onShownChange]);
  // The stage shows the revision the user opened in the history, else the item of the list.
  const item = revisions.shownItem;
  const nextNumber = useMemo(() => nextRevisionNumber(revisions.entries), [revisions.entries]);
  const revisionAction = useMemo(() => viewerRevisionAction(item), [item]);
  const openMusicVideoEditor = useOpenMusicVideoEditor(accountId);
  const editVideo = useCallback(() => {
    if (revisionAction.kind === 'editVideo') {
      openMusicVideoEditor(song.uuid, revisionAction.cutUuid);
    }
  }, [openMusicVideoEditor, revisionAction, song.uuid]);

  const label = useMemo(() => viewerAssetLabel(intl, item), [intl, item]);
  const plainShare = useAssetShare({ accountId, item, skip: isDemo, onToast: showToast });
  // design: trial-upsell a2. "Share link" and "Copy" of a watermarked file open the choice first.
  const choiceFile = useMemo(() => choiceFileOfViewerItem(item, label, song.title), [item, label, song.title]);
  const share = useGatedAssetShare(plainShare, item.watermark, choiceFile);
  const makeAgain = useMakeAgain(song.uuid);
  const { canPost, post } = useViewerPost({ song, allowReelPost, onClose });

  const move = useCallback(
    (delta: -1 | 1) => {
      const next = items[stepIndex(index, delta, count)];
      if (next !== undefined) {
        setCurrentKey(next.key);
      }
    },
    [count, index, items]
  );
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (video === null) {
      return;
    }
    if (video.paused) {
      video.play().catch(() => undefined);
      return;
    }
    video.pause();
  }, []);
  const { confirmOpen, cancelTurnOff } = share;
  const { compareKeys, dialogEntry, closeCompare, closeDialog, startDialog } = revisions;
  // Esc closes the layer on top first: the dialog, then Compare, then the link confirm, then the viewer.
  const escape = useCallback(() => {
    if (dialogEntry !== null) {
      closeDialog();
    } else if (compareKeys !== null) {
      closeCompare();
    } else if (confirmOpen) {
      cancelTurnOff();
    } else {
      onClose();
    }
  }, [cancelTurnOff, closeCompare, closeDialog, compareKeys, confirmOpen, dialogEntry, onClose]);
  const newRevision = useCallback(() => startDialog(), [startDialog]);
  const { entries: revisionEntries, stageKey } = revisions;
  const stageNumber = useMemo(
    () => revisionEntries.find((entry) => entry.key === stageKey)?.number,
    [revisionEntries, stageKey]
  );
  // ← and → do nothing while the new-revision dialog or Compare is open: a move would change the
  // item under the open layer.
  const moveUnlessLayer = useCallback(
    (delta: -1 | 1) => {
      if (dialogEntry !== null || compareKeys !== null) {
        return;
      }
      move(delta);
    },
    [compareKeys, dialogEntry, move]
  );
  useViewerKeys(rootRef, { onEscape: escape, onMove: moveUnlessLayer, onTogglePlay: togglePlay });
  useFocusTrap(rootRef);

  // The page under the viewer does not scroll while it is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const onVideoError = useCallback(() => setFailedKey(item.key), [item.key]);
  const stageLabel = stageNumber === undefined ? label : `${label} r${stageNumber}`;
  const songLine = useMemo(() => {
    const theme = intl.formatMessage(releaseThemeMessages[item.releaseThemeId].name);
    return [song.title, song.artistName, theme].filter((part) => part !== '').join(' · ');
  }, [intl, item.releaseThemeId, song.artistName, song.title]);

  return createPortal(
    <div
      ref={rootRef}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage(viewerMessages.dialogLabel, { name: label, title: song.title })}
      tabIndex={-1}
      data-testid="asset-viewer"
    >
      <ViewerStage
        item={item}
        label={stageLabel}
        gone={revisions.goneEntry === null ? undefined : <RevisionGoneBox entry={revisions.goneEntry} />}
        songTitle={song.title}
        index={index}
        count={count}
        isNarrow={isNarrow}
        videoRef={videoRef}
        onMove={move}
        onClose={onClose}
        onVideoError={onVideoError}
      />
      <ViewerSidePanel
        accountId={accountId}
        catalogItemUuid={song.uuid}
        item={item}
        label={label}
        songLine={songLine}
        songTitle={song.title}
        isNarrow={isNarrow}
        isDemo={isDemo}
        canPost={canPost(item)}
        share={isDemo ? null : share}
        videoFailed={failedKey === item.key}
        onPost={post}
        onNewRevision={newRevision}
        canMakeRevision={revisions.entries.length > 0}
        revisionAction={revisionAction}
        onEditVideo={editVideo}
        goneEntry={revisions.goneEntry}
        nextNumber={nextNumber}
        revisions={
          <RevisionList
            entries={revisions.entries}
            openKey={revisions.stageKey}
            currentUserId={currentUserId}
            isNarrow={isNarrow}
            isDemo={isDemo}
            isError={revisions.isError}
            onOpen={revisions.open}
            onCompare={revisions.compare}
            onRemake={revisionAction.kind === 'newRevision' ? startDialog : undefined}
          />
        }
      />
      <ViewerRevisionLayers
        accountId={accountId}
        song={song}
        item={item}
        label={label}
        entries={revisions.entries}
        compareKeys={compareKeys}
        dialogEntry={dialogEntry}
        currentUserId={currentUserId}
        onCloseCompare={closeCompare}
        onCloseDialog={closeDialog}
        onMoreOptions={makeAgain}
        onToast={showToast}
      />
      {toast !== null && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
      {confirmOpen && (
        <ViewerConfirmDialog isBusy={share.isTurningOff} onKeep={cancelTurnOff} onTurnOff={share.turnOff} />
      )}
    </div>,
    document.body
  );
};

/**
 * The asset viewer (design music24-asset-viewer a1..a6): a full-screen dialog over the page. The
 * file shows at full size, a video plays, and the side panel holds the facts, the actions and the
 * public link, and the revision history of the item (Releases v2, design d1–d5). ← and → move
 * through the items, Esc closes (the layer on top first), Space plays and pauses. The focus stays inside and goes back to the card on close. An empty list shows
 * nothing.
 */
const AssetViewerComponent: React.FC<AssetViewerProps> = ({
  startKey,
  accountId,
  song,
  items,
  onClose,
  allowReelPost,
  startRevision,
  startWithNewRevision,
  onShownChange,
}) =>
  items.length === 0 ? null : (
    <OpenViewer
      accountId={accountId}
      song={song}
      items={items}
      onClose={onClose}
      allowReelPost={allowReelPost}
      initialKey={startKey}
      startRevision={startRevision}
      startWithNewRevision={startWithNewRevision}
      onShownChange={onShownChange}
    />
  );

export const AssetViewer = TypedMemo(AssetViewerComponent);
